import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Privacy Policy | Barton</title>
        <meta name="description" content="Read Barton's privacy policy to understand how we collect, use, and protect your personal data in compliance with UK GDPR regulations." />
      </Helmet>
      <section className="container-custom py-12 md:py-16">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-8 text-white">
            Privacy Policy
          </h1>

          <div className="prose prose-invert max-w-none">
            <p className="text-white/60 text-lg mb-8">
              Last updated: December 2025
            </p>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Barton ("we", "our", or "us"), a trading name of VZV Ltd (Company No. 10689932), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website barton.uk.
              </p>
              <p className="text-white/70 leading-relaxed">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                We may collect information about you in a variety of ways. The information we may collect on the Site includes:
              </p>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Personal Data</h3>
              <p className="text-white/70 leading-relaxed mb-4">
                When you fill out our contact form, we collect personally identifiable information, such as your name, email address, and telephone number that you voluntarily give to us.
              </p>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Derivative Data</h3>
              <p className="text-white/70 leading-relaxed">
                Information our servers automatically collect when you access the Site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Site.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">3. Use of Your Information</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Respond to your enquiries and contact requests</li>
                <li>Send you information about our services</li>
                <li>Improve our website and services</li>
                <li>Monitor and analyze usage and trends</li>
                <li>Prevent fraudulent transactions and monitor against theft</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">4. Cookies and Tracking</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                We may use cookies, web beacons, tracking pixels, and other tracking technologies on the Site to help customize the Site and improve your experience. When you access the Site, your personal information is not collected through the use of tracking technology.
              </p>
              <p className="text-white/70 leading-relaxed mb-4">
                You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies through your browser settings.
              </p>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Types of Cookies We Use</h3>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">5. Disclosure of Your Information</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li><strong>By Law or to Protect Rights:</strong> If we believe the release of information is necessary to comply with the law</li>
                <li><strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us</li>
                <li><strong>Business Transfers:</strong> If we undergo a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">6. Security of Your Information</h2>
              <p className="text-white/70 leading-relaxed">
                We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that no security measures are perfect, and no method of data transmission can be guaranteed against interception or misuse.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">7. Your Rights</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Under the UK GDPR, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>The right to access your personal data</li>
                <li>The right to rectification of inaccurate data</li>
                <li>The right to erasure ('right to be forgotten')</li>
                <li>The right to restrict processing</li>
                <li>The right to data portability</li>
                <li>The right to object to processing</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">8. Contact Us</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                If you have questions or comments about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-[#111111] rounded-xl p-6 border border-white/10">
                <p className="text-white font-semibold mb-2">Barton (VZV Ltd)</p>
                <p className="text-white/70">Email: hello@barton.uk</p>
                <p className="text-white/70">Phone: 01704 777881</p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
